import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Wallet, Briefcase, Phone, Mail, Building2, Calendar, BadgeCheck, KeyRound } from "lucide-react";
import empApi from "../../lib/empApi";
import { useEmployeeAuth } from "../../context/EmployeeAuthContext";

const rp = (n) => "Rp " + Math.round(n || 0).toLocaleString("id-ID");

export default function EmpProfile() {
  const { employee, logout } = useEmployeeAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [payroll, setPayroll] = useState(null);

  useEffect(() => {
    empApi.get("/employee/profile").then(({ data }) => setProfile(data)).catch(() => {});
    empApi.get("/employee/payroll-preview").then(({ data }) => setPayroll(data)).catch(() => {});
  }, []);

  if (!profile) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-amber-400" /></div>;
  const e = profile.employee;

  const Field = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3 border-b border-slate-800 py-2.5 last:border-0">
      <Icon size={16} className="shrink-0 text-slate-500" />
      <span className="flex-1 text-xs text-slate-400">{label}</span>
      <span className="text-sm font-medium text-white">{value || "—"}</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="emp-profile-page">
      <div className="flex flex-col items-center pt-2 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-3xl font-bold text-slate-900">
          {(e.full_name || "?").charAt(0)}
        </div>
        <h2 className="mt-3 font-heading text-xl font-bold text-white">{e.full_name}</h2>
        <p className="text-sm text-slate-400">{e.position} · {e.employee_id}</p>
        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-0.5 text-xs font-semibold text-emerald-300"><BadgeCheck size={12} /> {e.status}</span>
      </div>

      {/* Payroll preview */}
      {payroll && (
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-indigo-900/40 to-slate-800/40 p-4" data-testid="emp-payroll-preview">
          <div className="mb-2 flex items-center gap-2 text-slate-300"><Wallet size={16} /> <span className="text-sm font-semibold">Pratinjau Payroll</span></div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-slate-400">Hari Kerja</p><p className="font-bold text-white">{payroll.working_days}/{payroll.expected_days}</p></div>
            <div><p className="text-xs text-slate-400">Kehadiran</p><p className="font-bold text-white">{payroll.attendance_pct}%</p></div>
            <div><p className="text-xs text-slate-400">Terlambat</p><p className="font-bold text-white">{payroll.late_minutes} mnt</p></div>
            <div><p className="text-xs text-slate-400">Lembur</p><p className="font-bold text-white">{payroll.overtime_hours} jam</p></div>
            <div><p className="text-xs text-slate-400">Skor KPI</p><p className="font-bold text-white">{payroll.kpi_score}</p></div>
            <div><p className="text-xs text-slate-400">Bonus</p><p className={`font-bold ${payroll.bonus_eligible ? "text-emerald-400" : "text-slate-400"}`}>{payroll.bonus_eligible ? "Layak" : "Belum"}</p></div>
          </div>
          {payroll.monthly_salary > 0 && (
            <div className="mt-3 border-t border-slate-700 pt-2">
              <div className="flex justify-between text-sm"><span className="text-slate-400">Estimasi (pratinjau)</span><span className="font-heading text-lg font-bold text-amber-400">{rp(payroll.estimated_net)}</span></div>
            </div>
          )}
          <p className="mt-2 text-[10px] text-slate-500">{payroll.note}</p>
        </div>
      )}

      {/* Profile fields */}
      <div className="rounded-2xl border border-slate-800 bg-slate-800/40 px-4 py-1" data-testid="emp-profile-fields">
        <Field icon={Building2} label="Cabang" value={e.branch_name} />
        <Field icon={Briefcase} label="Departemen" value={e.department} />
        <Field icon={Phone} label="Telepon" value={e.phone} />
        <Field icon={Mail} label="Email" value={e.email} />
        <Field icon={Calendar} label="Tanggal Bergabung" value={e.join_date} />
        <Field icon={Phone} label="Kontak Darurat" value={e.emergency_contact} />
      </div>

      {/* Jobdesk */}
      <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4" data-testid="emp-profile-jobdesk">
        <p className="mb-2 text-sm font-semibold text-slate-300">Tanggung Jawab ({e.position})</p>
        <ul className="space-y-1.5">
          {profile.jobdesk.responsibilities.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" /> {r}</li>
          ))}
        </ul>
      </div>

      <button onClick={() => navigate("/karyawan/ganti-pin")} data-testid="emp-profile-change-pin" className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/40 py-3 text-sm font-semibold text-slate-200">
        <KeyRound size={16} /> Ganti PIN
      </button>
      <button onClick={logout} data-testid="emp-profile-logout" className="w-full rounded-xl border border-red-500/30 bg-red-500/10 py-3 text-sm font-semibold text-red-300">Keluar</button>
    </div>
  );
}
